import { useLanguage } from "@/contexts/LanguageContext";

const PartnerCard = ({
  name,
  contact,
  description,
  logoPlaceholder,
  isMainOrganizer = false,
  isChurch = false,
}: {
  name: string;
  contact: string;
  description: string;
  logoPlaceholder: string;
  isMainOrganizer?: boolean;
  isChurch?: boolean;
}) => {
  const getContactLink = (contact: string) => {
    if (!contact) return null;
    
    if (contact.includes('@')) {
      return {
        href: `mailto:${contact}`,
        display: contact
      };
    }
    
    if (contact.match(/^\d/)) {
      const phoneNumber = contact.replace(/\s/g, '');
      return {
        href: `tel://+48${phoneNumber}`,
        display: `+48 ${phoneNumber}`
      };
    }
    
    // Website
    const website = contact.startsWith('www') ? contact : `www.${contact}`;
    return {
      href: `https://${website}`,
      display: website
    };
  };

  const contactInfo = getContactLink(contact);

  return (
    <div className={`p-8 rounded-3xl shadow-sm ${isMainOrganizer ? 'bg-red-50' : 'bg-card'}`}>
      {isChurch && (
        <div className="mb-4">
          <span className={`inline-block text-sm font-medium px-4 py-1.5 rounded-full ${
            isMainOrganizer 
              ? 'bg-red-600 text-white' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {isMainOrganizer ? 'Główny organizator' : 'Współorganizator'}
          </span>
        </div>
      )}
      <div className="min-h-[5rem]">
        <h3 className={`text-2xl font-bold leading-tight mb-4 ${isMainOrganizer ? 'text-red-700' : ''}`}>
          {name}
        </h3>
      </div>
      {contactInfo && (
        <div className={`text-base mb-4 ${isMainOrganizer ? 'text-red-600' : 'text-primary'}`}>
          <a
            href={contactInfo.href}
            className="hover:underline"
            target={contact.includes('@') || contact.match(/^\d/) ? undefined : "_blank"}
            rel={contact.includes('@') || contact.match(/^\d/) ? undefined : "noopener noreferrer"}
          >
            {contactInfo.display}
          </a>
        </div>
      )}
      <p className={`text-base leading-relaxed ${isMainOrganizer ? 'text-red-800' : 'text-muted-foreground'}`}>{description}</p>
    </div>
  );
};

const PartnersPage = () => {
  const { t } = useLanguage();

  const christianCommunities = [
    {
      name: "Kościół Chrześcijański Arka",
      contact: "arka.org.pl",
      description: "Wspólnota chrześcijańska prowadzona przez Pastora Mariana Rasska. Nabożeństwo: Niedziela, godz. 11:00, ul. Przybyszewskiego 56",
      logoPlaceholder: "ARKA",
    },
    {
      name: "Kościół Poznań Centrum",
      contact: "kbwch.poznancentrum@gmail.com",
      description: "Wspólnota chrześcijańska prowadzona przez Pastora Mariusza Sandera. Nabożeństwo: Niedziela, godz. 11:00, ul. Grochowska 49 B sala nr 100.",
      logoPlaceholder: "CPS",
    },
    {
      name: "Kościół Zielonoświątkowy \"Jeruzalem\" w Swarzędzu",
      contact: "kzswarzedz@gmail.com",
      description: "Kościół ZIelonoświątkowy prowadzony przez Pastora Tomasza Jarząba. Nabożeństwo: Niedziela, godz. 10:00, Nowowiejska 18, 62-020",
      logoPlaceholder: "KZJ",
    },
    {
      name: "Kościół Zielonoświątkowy Oborniki Wlkp",
      contact: "601165958",
      description: "Kościół Zielonoświątkowy prowadzony przez Pastora Dariusz Dudziński. Nabożeństwo: Niedziela, godz. 10:00, ul. Zamkowa 6, Oborniki.",
      logoPlaceholder: "KZO",
    },
    {
      name: "Kościół Baptystyczny Amazing Grace",
      contact: "amazinggrace.pl",
      description: "Kościół Baptystyczny prowadzony przez Pastora Andrzeja Górskiego. Nabożeństwa: Niedziela, 11:00, Eugeniusza Paukszty 1, Poznań",
      logoPlaceholder: "AG",
    },
    {
      name: "Kosciol Boży w Chrystusie \"Sól Ziemi\"",
      contact: "kosciolsolziemi.pl",
      description: "Kościół prowadzony przez Pastora Mariusza Jagodzińskiego. Nabożeństwo: Niedziela, 10:00, ul.Galileusza 8 (Klub Osiedlowy Kopernik)",
      logoPlaceholder: "SZ",
    },
    {
      name: "Kościół Zielonoświątkowy \"Nowa Nadzieja\"",
      contact: "kzpoznan.pl",
      description: "Kościół prowadzony przez Pastorów ⁠⁠Piotra Buczkowskiego i Piotra Wiselkę. Nabożeństwo: Niedziela, 10:00, ul. Piątkowska 167",
      logoPlaceholder: "NN",
    },
    {
      name: "Kościół baptystyczny Koinonia",
      contact: "koinonia.pl",
      description: "Kościół Baptystyczny prowadzony przez Pastora Dariusza Chydzika. w kaplicy w Poznaniu przy ul. Przemysłowej 48a Niedziela, godz. 11:00",
      logoPlaceholder: "KOI",
    },
    {
      name: "Kościół Boży w Chrystusie Koszalin",
      contact: "",
      description: "Kościół chrześcijański prowadzony przez Pastora Przemysława Piwowarczyka.",
      logoPlaceholder: "KBK",
    },
    {
      name: "Wspólnota Chrześcijańska w Czarnkowie",
      contact: "",
      description: "Lider wspólnoty Witold Dudek.",
      logoPlaceholder: "WCC",
    },
    {
      name: "Kościół Baptystyczny Genesis",
      contact: "genesis.org.pl",
      description: "Kościół baptystyczny prowadzony przez Pastora Roberta Bozydaja. Nabożeństwa niedzielne: 11:00, Dom Kultury Polan Sto, os.Polan 100, Poznań",
      logoPlaceholder: "GEN",
    },
  ];

  const otherOrganizations = [
    {
      name: "Boanerges",
      contact: "boanerges.pl",
      description: "Chrześcijański klub motocyklowy",
      logoPlaceholder: "BOA",
    },
    {
      name: "Absolutnie Fantastycznie",
      contact: "sklep.af.com.pl",
      description: "Chrześcijańska Księgarnia",
      logoPlaceholder: "AF",
    },
  ];

  return (
    <div className="pt-16">
      <div className="bg-secondary">
        <div className="container mx-auto py-16 px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t("partners.title")}
          </h1>
          <p className="text-xl text-primary">{t("partners.subtitle")}</p>
        </div>
      </div>

      <div className="container mx-auto py-12 px-4 md:px-8">
        <div className="relative">
          <h2 className="text-3xl font-bold mb-8">Współnoty chrześcijańskie</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {christianCommunities.map((partner, index) => (
              <PartnerCard
                key={index}
                name={partner.name}
                contact={partner.contact}
                description={partner.description}
                logoPlaceholder={partner.logoPlaceholder}
                isMainOrganizer={partner.name === "Kościół Chrześcijański Arka"}
                isChurch={true}
              />
            ))}
          </div>

          <h2 className="text-3xl font-bold mb-8">Firmy i inne organizacje</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherOrganizations.map((partner, index) => (
              <PartnerCard
                key={index}
                name={partner.name}
                contact={partner.contact}
                description={partner.description}
                logoPlaceholder={partner.logoPlaceholder}
                isChurch={false}
              />
            ))}
          </div>
        </div>

        {/* Patron medialny */}
        <h2 className="text-3xl font-bold mb-8 mt-16">Patron medialny</h2>
        <div className="flex items-center mb-16">
          <a
            href="https://konin24.info/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Konin24.info"
            className="block"
          >
            <img
              src="/konin.png"
              alt="Konin24.info logo"
              className="h-40 w-auto object-contain"
              style={{ maxWidth: 480 }}
            />
          </a>
        </div>

        <div className="mt-16 p-8 bg-secondary rounded-lg text-center">
          <h2 className="text-2xl font-bold mb-4">{t("partners.become")}</h2>
          <p className="mb-6 max-w-2xl mx-auto">
            {t("partners.become.description")}
          </p>
          <div className="flex justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring bg-primary text-primary-foreground shadow hover:bg-primary/90 px-8 py-3"
            >
              {t("nav.contact")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnersPage;
